// WebAudio sound synthesis for the Ludo client.
// Ported verbatim from ludo_client.php's playXxxSound() functions — every
// frequency, duration, waveform and gain ramp matches the PHP source so the
// game feels identical to the original.
//
// A single shared AudioContext is lazily created on first use (browsers
// require a user gesture before playback). If the context ever lands in the
// "suspended" state we resume it inline so events that fire from WS messages
// still sound.

let _ctx: AudioContext | null = null;
let _enabled = true;

// Shame-tone bookkeeping (used while a captured token retreats to its yard).
let _shameOsc: OscillatorNode | null = null;
let _shameGain: GainNode | null = null;

// Urgency-beep ticker (alternating blips during the last seconds of a turn).
let _urgencyTimer: number | null = null;
let _urgencyTick = 0;

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx && _ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

export function setSoundEnabled(on: boolean) {
  _enabled = on;
  if (!on) stopShameTone();
  if (!on) stopUrgencyBeep();
}

export function isSoundEnabled() {
  return _enabled;
}

export function primeAudio() {
  const c = ctx();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
}

let _unlockInstalled = false;
export function installAudioUnlock() {
  if (_unlockInstalled) return;
  if (typeof document === 'undefined') return;
  _unlockInstalled = true;
  const handler = () => {
    const c = ctx();
    if (!c) return;
    if (c.state === 'suspended') c.resume().catch(() => {});
    try {
      const osc = c.createOscillator();
      const gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, c.currentTime);
      osc.connect(gain).connect(c.destination);
      osc.start();
      osc.stop(c.currentTime + 0.02);
    } catch {}
    document.removeEventListener('pointerdown', handler, true);
    document.removeEventListener('touchstart', handler, true);
    document.removeEventListener('keydown', handler, true);
  };
  document.addEventListener('pointerdown', handler, true);
  document.addEventListener('touchstart', handler, true);
  document.addEventListener('keydown', handler, true);
}

// ─── Dice roll — 8 quick random square blips. ────────────────────────
// PHP: 600 + rand*800 (600-1400Hz), gain 0.1, duration 0.03s, delay i*0.05.
export function playDiceRollSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const t0 = c.currentTime;
  for (let i = 0; i < 8; i++) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'square';
    const t = t0 + i * 0.05;
    osc.frequency.setValueAtTime(600 + Math.random() * 800, t);
    gain.gain.setValueAtTime(0.1, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.03);
  }
}

// ─── Jump (per-step token hop). ──────────────────────────────────────
// PHP: sine 400→600Hz over 0.1s, gain 0.1→0.01.
export function playJumpSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(400, t0);
  osc.frequency.exponentialRampToValueAtTime(600, t0 + 0.1);
  gain.gain.setValueAtTime(0.1, t0);
  gain.gain.exponentialRampToValueAtTime(0.01, t0 + 0.1);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.1);
}

// ─── Capture (enemy sent back to yard). ──────────────────────────────
// PHP: sawtooth 300→100Hz over 0.4s, gain 0.1→0.01.
export function playCaptureSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sawtooth';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(300, t0);
  osc.frequency.exponentialRampToValueAtTime(100, t0 + 0.4);
  gain.gain.setValueAtTime(0.1, t0);
  gain.gain.exponentialRampToValueAtTime(0.01, t0 + 0.4);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.4);
}

// ─── Reached home (stepped 400→600→800Hz rise on a single osc). ──────
// PHP: sine, 3 setValueAtTime calls at 0, 0.1, 0.2s with linear gain
// ramp 0.1→0.01 over 0.4s. Using one oscillator with step-frequency
// changes, not three separate blips — distinct "chime" timbre.
export function playHomeSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(400, t0);
  osc.frequency.setValueAtTime(600, t0 + 0.1);
  osc.frequency.setValueAtTime(800, t0 + 0.2);
  gain.gain.setValueAtTime(0.1, t0);
  gain.gain.linearRampToValueAtTime(0.01, t0 + 0.4);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.4);
}

// ─── Safe-zone cue. ──────────────────────────────────────────────────
// PHP: triangle, freq set 523.25 then 659.25 at +0.1s (step change, not
// glissando), gain 0.1→0.01 exponential over 0.3s.
export function playSafeZoneSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(523.25, t0);
  osc.frequency.setValueAtTime(659.25, t0 + 0.1);
  gain.gain.setValueAtTime(0.1, t0);
  gain.gain.exponentialRampToValueAtTime(0.01, t0 + 0.3);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.3);
}

// ─── Shame tone (sustained while captured token retreats). ───────────
// PHP: triangle, starts at 350Hz, gain 0.1 set immediately (no fade-in).
export function startShameTone() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  stopShameTone();
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(350, c.currentTime);
  gain.gain.setValueAtTime(0.1, c.currentTime);
  osc.connect(gain).connect(c.destination);
  osc.start();
  _shameOsc = osc;
  _shameGain = gain;
}

// PHP: shameOsc.frequency.exponentialRampToValueAtTime(Math.max(100, 350 - step*5), +0.1s).
// `step` is the 1-based retreat-step index so the tone bottoms out around step ~50.
export function dropShameTone(step: number = 1) {
  const c = ctx(); if (!c || !_shameOsc) return;
  const target = Math.max(100, 350 - step * 5);
  _shameOsc.frequency.exponentialRampToValueAtTime(target, c.currentTime + 0.1);
}

// PHP: shameGain.gain.exponentialRampToValueAtTime(0.001, +0.5s); osc.stop(+0.5s).
export function stopShameTone() {
  const c = ctx();
  if (!c || !_shameOsc || !_shameGain) {
    _shameOsc = null; _shameGain = null; return;
  }
  const now = c.currentTime;
  _shameGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  try { _shameOsc.stop(now + 0.5); } catch {}
  _shameOsc = null;
  _shameGain = null;
}

// ─── Urgency beep ticker (last seconds of turn). ─────────────────────
// PHP: 600ms interval; square osc, alternating 880/1320Hz; gain ramps
// 0.0001→0.18 (+0.01s) then →0.0001 (+0.14s); osc.stop at +0.16s.
export function startUrgencyBeep() {
  if (!_enabled) return;
  if (_urgencyTimer != null) return;
  _urgencyTick = 0;
  const tick = () => {
    const c = ctx(); if (!c) return;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'square';
    const f = (_urgencyTick % 2 === 0) ? 880 : 1320;
    const t0 = c.currentTime;
    osc.frequency.setValueAtTime(f, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    osc.connect(gain).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.16);
    _urgencyTick++;
  };
  tick();
  _urgencyTimer = window.setInterval(tick, 600);
}

export function stopUrgencyBeep() {
  if (_urgencyTimer != null) {
    clearInterval(_urgencyTimer);
    _urgencyTimer = null;
  }
}

// ─── Kick (two-layer descending thud). ───────────────────────────────
// PHP layer 1: sawtooth 420→60Hz over 0.55s, gain 0.22→0.001 over 0.6s,
// osc.stop at 0.6s. Layer 2: triangle 180→40Hz over 0.5s, gain 0.14→0.001
// over 0.55s, osc.stop at 0.55s. Both start at the same t0.
export function playKickSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const t0 = c.currentTime;
  // Layer 1
  const o1 = c.createOscillator();
  const g1 = c.createGain();
  o1.type = 'sawtooth';
  o1.frequency.setValueAtTime(420, t0);
  o1.frequency.exponentialRampToValueAtTime(60, t0 + 0.55);
  g1.gain.setValueAtTime(0.22, t0);
  g1.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
  o1.connect(g1).connect(c.destination);
  o1.start(t0);
  o1.stop(t0 + 0.6);
  // Layer 2
  const o2 = c.createOscillator();
  const g2 = c.createGain();
  o2.type = 'triangle';
  o2.frequency.setValueAtTime(180, t0);
  o2.frequency.exponentialRampToValueAtTime(40, t0 + 0.5);
  g2.gain.setValueAtTime(0.14, t0);
  g2.gain.exponentialRampToValueAtTime(0.001, t0 + 0.55);
  o2.connect(g2).connect(c.destination);
  o2.start(t0);
  o2.stop(t0 + 0.55);
}

// ─── Win fanfare (C-E-G-C ascending). ────────────────────────────────
// PHP: four triangle notes, each at t0 + i*0.14; each note has
// gain 0.0001→0.18 (+0.02s) → 0.001 (+0.4s); osc.stop at +0.45s.
export function playWinFanfare() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  const t0 = c.currentTime;
  notes.forEach((f, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'triangle';
    const t = t0 + i * 0.14;
    osc.frequency.setValueAtTime(f, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.45);
  });
}

// ─── UI tap — tactile click for bet tile / settings / player-count. ──
// Very short, low volume so it reads as feedback, not noise.
export function playTap() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(1200, t0);
  gain.gain.setValueAtTime(0.06, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}

// ─── Slot-lock bloop — a player (real or filled) has joined a slot. ──
// Three rising sine beats, pleasant and quick, paired with the CSS
// pop animation already on `.wait-slot.locked`.
export function playSlotLock() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const t0 = c.currentTime;
  const notes = [
    { f: 523.25, dt: 0.00 }, // C5
    { f: 659.25, dt: 0.06 }, // E5
    { f: 880.00, dt: 0.12 }, // A5
  ];
  for (const n of notes) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    const ts = t0 + n.dt;
    osc.frequency.setValueAtTime(n.f, ts);
    gain.gain.setValueAtTime(0.0001, ts);
    gain.gain.exponentialRampToValueAtTime(0.12, ts + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ts + 0.14);
    osc.connect(gain).connect(c.destination);
    osc.start(ts);
    osc.stop(ts + 0.16);
  }
}

// ─── Your-turn chime — warm bell "ding" when the turn becomes yours.
// Designed to be timbrally DISTINCT from every other sound in the
// game so the player recognises it instantly:
//   - urgency beep = square 880/1320Hz, 600ms repeating → harsh, fast, alarm-y
//   - your-turn    = sine C5+G5 perfect-fifth chord + sine-overtone,
//                    soft attack, long bell-like decay → warm, friendly, single "ding"
// Two sines struck together give a bell-ish ring; the perfect-fifth
// interval (C5 + G5) is consonant and pleasant without sounding
// melodic. A tiny 1568Hz overtone at low gain adds sparkle and pushes
// the timbre away from anything else in the Ludo soundbank.
export function playYourTurnChime() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const t0 = c.currentTime;
  const partials = [
    { f: 523.25, gain: 0.11 }, // C5 — fundamental
    { f: 783.99, gain: 0.09 }, // G5 — perfect fifth
    { f: 1568.0, gain: 0.03 }, // G6 — airy sparkle
  ];
  for (const p of partials) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(p.f, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    // Soft attack (15ms), long bell-like decay (600ms).
    gain.gain.exponentialRampToValueAtTime(p.gain, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
    osc.connect(gain).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.65);
  }
}

// ─── Cancel / forfeit buzzer. ─────────────────────────────────────────
// Played when a turn is invalidated (e.g. three sixes in a row = turn
// forfeit). Two short low square blips on a falling pitch — a "nope"
// sound. Not a PHP port; the PHP reference had no distinct cue for
// this case so we synthesise one that pairs with the ❌ overlay shown
// over the dice face.
export function playCancelSound() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const t0 = c.currentTime;
  const steps = [
    { f: 380, start: 0.00, dur: 0.13 },
    { f: 220, start: 0.14, dur: 0.18 },
  ];
  for (const s of steps) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'square';
    const ts = t0 + s.start;
    osc.frequency.setValueAtTime(s.f, ts);
    gain.gain.setValueAtTime(0.18, ts);
    gain.gain.exponentialRampToValueAtTime(0.001, ts + s.dur);
    osc.connect(gain).connect(c.destination);
    osc.start(ts);
    osc.stop(ts + s.dur);
  }
}

// ─── Loss tone (C-Bb-G-Eb descending). ───────────────────────────────
// PHP: four sine notes, each at t0 + i*0.18; each note has
// gain 0.0001→0.15 (+0.02s) → 0.001 (+0.5s); osc.stop at +0.55s.
export function playLossTone() {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const notes = [523.25, 466.16, 392.0, 311.13]; // C5 Bb4 G4 Eb4
  const t0 = c.currentTime;
  notes.forEach((f, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    const t = t0 + i * 0.18;
    osc.frequency.setValueAtTime(f, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.15, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.55);
  });
}
